/**
 * Grabacion de la muestra de voz desde el kiosco.
 *
 * El servidor no tiene microfono, asi que la muestra para clonar la voz se
 * graba en el navegador de otro ordenador (o del movil) y se sube. Reutiliza
 * la captura de `lib/audio.ts`, que ya entrega PCM 16 kHz mono 16 bits, y
 * aqui solo se acumula y se envuelve en una cabecera WAV.
 *
 * 16 kHz es la frecuencia del kiosco (la que quiere el reconocimiento de voz)
 * y sirve de sobra para clonar: el motor remuestrea a lo que necesite.
 */

/** Frecuencia de la captura del kiosco. */
const FRECUENCIA = 16_000;

/**
 * Envuelve PCM 16 bits mono en un fichero WAV.
 *
 * @param trozos - fragmentos de PCM tal como los entrega la captura.
 * @param frecuencia - muestras por segundo del audio.
 * @returns El WAV completo, listo para subir.
 */
export function montarWav(trozos: ArrayBuffer[], frecuencia = FRECUENCIA): ArrayBuffer {
  const bytesDatos = trozos.reduce((total, t) => total + t.byteLength, 0);
  const bufer = new ArrayBuffer(44 + bytesDatos);
  const vista = new DataView(bufer);

  const texto = (posicion: number, valor: string) => {
    for (let i = 0; i < valor.length; i += 1) vista.setUint8(posicion + i, valor.charCodeAt(i));
  };

  texto(0, 'RIFF');
  vista.setUint32(4, 36 + bytesDatos, true);
  texto(8, 'WAVE');
  texto(12, 'fmt ');
  vista.setUint32(16, 16, true); // tamano del bloque fmt
  vista.setUint16(20, 1, true); // PCM sin comprimir
  vista.setUint16(22, 1, true); // mono
  vista.setUint32(24, frecuencia, true);
  vista.setUint32(28, frecuencia * 2, true); // bytes por segundo
  vista.setUint16(32, 2, true); // alineacion de bloque
  vista.setUint16(34, 16, true); // bits por muestra
  texto(36, 'data');
  vista.setUint32(40, bytesDatos, true);

  let posicion = 44;
  for (const trozo of trozos) {
    new Uint8Array(bufer).set(new Uint8Array(trozo), posicion);
    posicion += trozo.byteLength;
  }
  return bufer;
}

/**
 * Segundos de audio que suman unos trozos de PCM 16 bits.
 *
 * Sirve para el contador de la pantalla y para no dejar subir una grabacion
 * que el backend va a rechazar por corta.
 */
export function duracionDeTrozos(trozos: ArrayBuffer[], frecuencia = FRECUENCIA): number {
  const muestras = trozos.reduce((total, t) => total + t.byteLength, 0) / 2;
  return muestras / frecuencia;
}
